import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
    AllCommunityModule,
    ClientSideRowModelModule,
    ColDef,
    IGetRowsParams,
    ModuleRegistry,
    RowModelType
} from 'ag-grid-community';
import HLButton, { HLButtonStyleType } from '../button/HLButton';
import HLFormInput from '../input/HLFormInput';
import HLFormSelect from '../select/HLFormSelect';
import type { Filter, PageRequest, PageResponse } from '../../types/page';
import classes from './HLPageTable.module.scss'
import { AG_GRID_LOCALE_RU } from "../../styles/locale/locale.ru";
import HLTextButton from "../text-button/HLTextButton";
import HLModal from '../modal/HLModal';
import HLInfoRow from '../info-row/HLInfoRow';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

export type HLPageTableFilterConfig<T> = {
    fieldName: string;
    label: string;
    operator: '=' | 'like' | 'is';
    type?: 'input' | 'select' | 'date';
    options?: { value: string; label: string; }[];
    required?: boolean;
    defaultValue?: string;
};

export type HLPageTableActionButton = {
    label: string;
    onClick: () => void;
    styleType?: HLButtonStyleType;
};

export type HLPageTableRowActionButton = {
    getLabel: (params: any) => string;
    onClick: (params: any) => void;
};

interface HLPageTableProps<T> {
    title: string;
    columns: ColDef<T>[];
    defaultColDef: ColDef<T>;
    filterConfig: HLPageTableFilterConfig<T>[];
    actionButtons: HLPageTableActionButton[];
    rowActionButtons?: HLPageTableRowActionButton[];
    rowActionButtonsMinWidth?: number;
    fetchData: (request: PageRequest) => Promise<PageResponse<T>>;
    height?: number;
    rowHeight?: number;
    onRowClick?: (params: any) => void;
    onRowDoubleClick?: (params: any) => void;
    showStatisticsButton?: boolean;
    rowModelType?: RowModelType
}

interface HLPageTableStatisticModalProp {
    label: string;
    value: number;
}

export interface HLPageTableRef {
    refreshData: () => void;
}

const HLPageTable = forwardRef<HLPageTableRef, HLPageTableProps<any>>(({
                                                                           title,
                                                                           columns,
                                                                           defaultColDef,
                                                                           filterConfig,
                                                                           actionButtons,
                                                                           rowActionButtons,
                                                                           rowActionButtonsMinWidth,
                                                                           fetchData,
                                                                           height,
                                                                           rowHeight,
                                                                           onRowClick,
                                                                           onRowDoubleClick,
                                                                           showStatisticsButton = false,
                                                                           rowModelType
                                                                       }, ref) => {
    const gridRef = useRef<AgGridReact>(null);

    const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
    const [stats, setStats] = useState<HLPageTableStatisticModalProp[]>([]);

    const [filterValues, setFilterValues] = useState<{ [key: string]: string }>({});
    const [appliedFilters, setAppliedFilters] = useState<Filter[]>([]);

    const [errorFields, setErrorFields] = useState<Set<string>>(new Set());
    const [rowData, setRowData] = useState<any[]>([]);

    const setDefaultFilters = () => {
        const defaultValues: { [key: string]: string } = {};
        filterConfig.forEach(filter => {
            if (filter.defaultValue) defaultValues[filter.fieldName] = filter.defaultValue;
        });
        setFilterValues(defaultValues);

        // Автоматически применяем фильтры с дефолтными значениями
        if (Object.keys(defaultValues).length > 0) {
            const initialFilters: Filter[] = filterConfig
                .filter(filter => defaultValues[filter.fieldName] && defaultValues[filter.fieldName].trim() !== '')
                .map(filter => ({
                    fieldName: filter.fieldName,
                    operator: filter.operator,
                    fieldValue: defaultValues[filter.fieldName]
                }));
            setAppliedFilters(initialFilters);
        }
    }

    useEffect(() => {
        setDefaultFilters();
    }, [filterConfig]);

    useImperativeHandle(ref, () => ({
        refreshData: () => {
            if (gridRef.current) {
                gridRef.current.api.refreshInfiniteCache();
            }
        }
    }));

    // Кнопка статистики
    const handleShowStatistics = () => {
        if (!gridRef.current || !gridRef.current.api) return;
        const api = gridRef.current.api;

        const numericColumns = columns.filter(col => col.type === 'number');

        const sums: { [key: string]: number } = {};
        numericColumns.forEach(col => {
            sums[col.field as string] = 0;
        });

        api.forEachNode((node: any) => {
            numericColumns.forEach(col => {
                const field = col.field;

                let value = node.data && field ? node.data[field] : undefined;
                if (col.valueGetter && node) {
                    value = typeof col.valueGetter === 'function' ? col.valueGetter(node) : value;
                }

                if (typeof value === 'number') {
                    sums[field as string] += value;
                }
            });
        });

        setStats(numericColumns.map(col => ({
            label: col.headerName as string,
            value: sums[col.field as string] || 0
        })));

        setIsStatsModalOpen(true);
    };

    let finalActionButtons = [...actionButtons];
    if (showStatisticsButton) {
        finalActionButtons.push({
            label: 'Статистика',
            onClick: handleShowStatistics,
            styleType: HLButtonStyleType.GRAY
        })
    }

    const finalColumns = [...columns]
    if (rowActionButtons?.length) {
        finalColumns.push({
            headerName: 'Действия',
            autoHeight: true,
            minWidth: rowActionButtonsMinWidth,
            cellRenderer: (params: any) => {
                // Разбиваем массив кнопок на группы по 2
                const buttonGroups = [];
                for (let i = 0; i < rowActionButtons.length; i += 2) {
                    buttonGroups.push(rowActionButtons.slice(i, i + 2));
                }

                return (
                    <div className={classes.rowActionButtonContainer}>
                        {buttonGroups.map((group, groupIndex) => (
                            <div key={groupIndex} className={classes.rowActionButtonGroup}>
                                {group.map((button, buttonIndex) => (
                                    <HLTextButton
                                        key={buttonIndex}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            button.onClick(params)
                                        }}
                                    >
                                        {button.getLabel(params)}
                                    </HLTextButton>
                                ))}
                            </div>
                        ))}
                    </div>
                );
            },
        })
    }

    const handleFilterValueChange = (fieldName: string, fieldValue: string) => {
        setFilterValues(previous => ({...previous, [fieldName]: fieldValue}));

        if (filterConfig.find(filter => fieldName === filter.fieldName && filter.required)) {
            setErrorFields(previous => {
                if (fieldValue) previous.delete(fieldName)
                else previous.add(fieldName)
                return previous;
            });
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleApplyFilters();
    };

    const handleApplyFilters = () => {
        const missingRequiredFields = filterConfig
            .filter(filter => filter.required)
            .filter(filter => !filterValues[filter.fieldName] || filterValues[filter.fieldName].trim() === '')
        if (missingRequiredFields.length > 0) {
            setErrorFields(new Set(missingRequiredFields.map(field => field.fieldName)));
            return;
        }
        setErrorFields(new Set());

        const filters: Filter[] = filterConfig
            .filter(filter => filterValues[filter.fieldName] && filterValues[filter.fieldName].trim() !== '')
            .map(filter => ({
                fieldName: filter.fieldName,
                operator: filter.operator,
                fieldValue: filterValues[filter.fieldName]
            }));
        setAppliedFilters(filters);
    };

    const handleClearFilters = () => {
        setFilterValues({});
        setAppliedFilters([]);
        setErrorFields(new Set());
        setDefaultFilters();
    };

    useEffect(() => {
        if (rowModelType !== 'clientSide') return

        fetchData({
            filters: appliedFilters
        }).then(response => setRowData(response.content))
    }, [appliedFilters, fetchData]);

    const datasource = useMemo(() => {
        if (rowModelType && rowModelType !== 'infinite') return

        return {
            getRows: async (params: IGetRowsParams) => {
                try {
                    const response = await fetchData({
                        filters: appliedFilters,
                        page: {
                            page: Math.floor(params.startRow / 20),
                            size: params.endRow - params.startRow
                        }
                    });
                    params.successCallback(response.content, response.totalElements);
                } catch (error) {
                    params.failCallback();
                }
            }
        }
    }, [appliedFilters, fetchData]);

    return (
        <div className={classes.pageContainer} style={{height: height}}>
            <h2>{title}</h2>

            {finalActionButtons.length > 0 &&
                <div className={`inlineContainer ${classes.actionContainer}`}>
                    {finalActionButtons.map((button, index) => (
                        <HLButton
                            key={index}
                            onClick={button.onClick}
                            styleType={button.styleType}
                        >
                            {button.label}
                        </HLButton>
                    ))}
                </div>
            }

            {filterConfig.length > 0 &&
                <>
                    <div className={`inlineContainer ${classes.actionContainer}`}>
                        {filterConfig.map((filter) => {
                            if (filter.type === 'select' && filter.options) {
                                return (
                                    <HLFormSelect
                                        key={filter.fieldName}
                                        id={filter.fieldName}
                                        value={filterValues[filter.fieldName] ?? ''}
                                        onChange={e => handleFilterValueChange(filter.fieldName, e.target.value)}
                                        onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e)}
                                        options={[
                                            {value: '', label: filter.label},
                                            ...filter.options
                                        ]}
                                    />
                                );
                            }

                            return (
                                <HLFormInput
                                    key={filter.fieldName}
                                    id={filter.fieldName}
                                    type={filter.type}
                                    value={filterValues[filter.fieldName] ?? ''}
                                    onChange={e => handleFilterValueChange(filter.fieldName, e.target.value)}
                                    onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e)}
                                    placeholder={filter.label}
                                    error={errorFields.has(filter.fieldName) ? `Обязательное поле ` : ''}
                                />
                            );
                        })}
                    </div>

                    <div className={`inlineContainer ${classes.actionContainer}`}>
                        <HLButton onClick={handleApplyFilters} type="button">
                            Применить фильтры
                        </HLButton>
                        <HLButton onClick={handleClearFilters} styleType={HLButtonStyleType.GRAY} type="button">
                            Очистить фильтры
                        </HLButton>
                    </div>
                </>
            }

            <div className={classes.gridWrapper}>
                <AgGridReact
                    ref={gridRef}
                    className="ag-theme-alpine"
                    localeText={AG_GRID_LOCALE_RU}
                    overlayLoadingTemplate="Загрузка данных..."
                    columnDefs={finalColumns}
                    defaultColDef={defaultColDef}
                    rowBuffer={0}
                    rowHeight={rowHeight}
                    rowModelType={rowModelType || "infinite"}
                    cacheBlockSize={20}
                    cacheOverflowSize={2}
                    maxConcurrentDatasourceRequests={1}
                    infiniteInitialRowCount={20}
                    maxBlocksInCache={10}
                    onRowClicked={onRowClick}
                    onRowDoubleClicked={onRowDoubleClick}
                    datasource={datasource}
                    rowData={rowData}
                />
            </div>

            <HLModal
                open={isStatsModalOpen}
                onClose={() => setIsStatsModalOpen(false)}
                title="Статистика">
                {
                    stats.map(stat =>
                        <HLInfoRow key={stat.label} label={stat.label} value={stat.value}/>
                    )
                }
            </HLModal>
        </div>
    );
});

export default HLPageTable; 